/* ===========================================================================
 * swatch-cascade.js — Selector de variantes en cascada (jerárquico)
 * ---------------------------------------------------------------------------
 * Una opción "manda" (la de mayor prioridad) y las demás se filtran por las de
 * mayor prioridad. Mantiene precio/carrito/URL moviendo los <select> nativos
 * del tema y publica las imágenes del color para la galería.
 *
 * Eventos:
 *   - emite   "variant:change"       → otros componentes
 *   - emite   "gallery:update"       → galería (imágenes de los colores disponibles)
 *   - escucha "gallery:select-color" → selecciona el color al navegar la galería
 *
 * Config leída del DOM (por sección):
 *   .product__cascade-swatch[data-section-id][data-option-index]
 *   <script id="swatch-config-{id}">            { selectedId, optionNames, optionOrder }
 *   <script id="cascade-variants-{id}">         [ { id, options, available, price } ]
 *   <script id="gallery-images-by-color-{id}">  { color: [ { id, alt, w, h, thumb, src } ] }
 * ======================================================================== */
(() => {
  "use strict";

  const GROUP_SELECTOR = ".product__cascade-swatch[data-section-id]";
  const isColorName = (name) => name.toLowerCase().includes("colo");

  const readJSON = (id) => {
    const node = document.getElementById(id);
    if (!node) return null;
    try { return JSON.parse(node.textContent); } catch (error) { return null; }
  };

  const triggerChange = (element) => {
    if (!element) return;
    if (window.jQuery) window.jQuery(element).trigger("change");
    else element.dispatchEvent(new Event("change", { bubbles: true }));
  };

  class SwatchCascade {
    constructor(sectionId) {
      this.sectionId = sectionId;

      const config = readJSON(`swatch-config-${sectionId}`) || {};
      this.optionNames = config.optionNames || [];
      this.optionOrder = config.optionOrder || [];
      this.selectedVariantId = config.selectedId;

      this.variants = readJSON(`cascade-variants-${sectionId}`) || [];
      this.imagesByColor = readJSON(`gallery-images-by-color-${sectionId}`) || {};
      this.colorIndex = this.optionNames.findIndex(isColorName);
      this.optionCount = this.optionNames.length;

      this.groups = {};          // optionIndex -> elemento .product__cascade-swatch
      this.priorityOrder = [];   // optionIndex[] ordenado por prioridad (mayor primero)
      this.priorityRank = {};    // optionIndex -> posición en priorityOrder
      this.selectedValues = [];  // valor elegido en cada optionIndex
      this.masterSelect = null;
      this.suppressGalleryEmit = false; // true cuando el cambio viene de la galería
      this.firstGroup = null;

      this.handleGallerySelectColor = this.handleGallerySelectColor.bind(this);
    }

    /* --------------------------------------------------- ciclo de vida --- */
    init() {
      const groups = Array.from(
        document.querySelectorAll(`.product__cascade-swatch[data-section-id="${this.sectionId}"]`)
      );
      if (!groups.length || !this.variants.length) return false;

      this.firstGroup = groups[0];
      this.masterSelect = document.getElementById(`product_select_${this.sectionId}`);
      groups.forEach((group) => { this.groups[Number(group.dataset.optionIndex)] = group; });

      this.buildPriorityOrder();
      this.reorderGroupsInDom();
      this.seedSelectionFromCurrentVariant();
      this.bindEvents();

      this.cascadeFrom(0);     // estado inicial: filtra desde la opción de mayor prioridad
      this.syncThemeAndUrl();
      this.emitGalleryUpdate(); // publica las imágenes del color inicial
      return true;
    }

    destroy() {
      document.removeEventListener("gallery:select-color", this.handleGallerySelectColor);
    }

    /* ------------------------------------------ prioridad y orden visual --- */
    // Convierte optionOrder (nombres) en índices reales; añade al final las no
    // listadas, para no romper si hay nombres erróneos.
    buildPriorityOrder() {
      this.optionOrder.forEach((name) => {
        const index = this.optionNames.indexOf(name);
        if (index !== -1 && !this.priorityOrder.includes(index)) this.priorityOrder.push(index);
      });
      for (let i = 0; i < this.optionCount; i++) {
        if (!this.priorityOrder.includes(i)) this.priorityOrder.push(i);
      }
      this.priorityOrder.forEach((index, position) => { this.priorityRank[index] = position; });
    }

    // Reordena los grupos en pantalla para que sigan el orden de prioridad.
    reorderGroupsInDom() {
      this.priorityOrder.forEach((index) => {
        const group = this.groups[index];
        if (!group) return;
        const wrapper = group.closest(".product__options") || group;
        wrapper.parentNode?.appendChild(wrapper);
      });
    }

    // Selección inicial = variante seleccionada / primera disponible.
    seedSelectionFromCurrentVariant() {
      this.selectedValues = new Array(this.optionCount).fill(null);
      const seed = this.findVariantById(this.selectedVariantId) || this.variants[0];
      if (seed) seed.options.forEach((value, index) => { this.selectedValues[index] = value; });
    }

    /* --------------------------------------- consultas sobre las variantes --- */
    // ¿La variante encaja con lo elegido en las opciones de MAYOR prioridad que
    // `optionIndex`? (Las de menor prioridad no la limitan.)
    matchesHigherPriority(variant, optionIndex) {
      for (let other = 0; other < this.optionCount; other++) {
        if (other === optionIndex) continue;
        const isHigher = this.priorityRank[other] < this.priorityRank[optionIndex];
        if (isHigher && this.selectedValues[other] != null &&
            variant.options[other] !== this.selectedValues[other]) {
          return false;
        }
      }
      return true;
    }

    valueExists(optionIndex, value) {
      return this.variants.some(
        (variant) => this.matchesHigherPriority(variant, optionIndex) && variant.options[optionIndex] === value
      );
    }

    valueInStock(optionIndex, value) {
      return this.variants.some(
        (variant) => this.matchesHigherPriority(variant, optionIndex) &&
          variant.options[optionIndex] === value && variant.available
      );
    }

    // Valores posibles en una opción, dadas las de mayor prioridad.
    possibleValues(optionIndex) {
      const values = [];
      this.variants.forEach((variant) => {
        if (!this.matchesHigherPriority(variant, optionIndex)) return;
        const value = variant.options[optionIndex];
        if (!values.includes(value)) values.push(value);
      });
      return values;
    }

    findVariantById(id) {
      return this.variants.find((variant) => variant.id === id) || null;
    }

    // Variante que coincide exactamente con la selección actual completa.
    findSelectedVariant() {
      return this.variants.find(
        (variant) => variant.options.every((value, index) => value === this.selectedValues[index])
      ) || null;
    }

    /* ------------------------------------------------ cascada (estado/pintado) --- */
    // Si el valor elegido ya no es válido, salta al primero con stock (o al
    // primero que exista).
    repairSelection(optionIndex) {
      const current = this.selectedValues[optionIndex];
      const stillValid = current != null && this.valueExists(optionIndex, current) && this.valueInStock(optionIndex, current);
      if (stillValid) return;

      const values = this.possibleValues(optionIndex);
      const withStock = values.filter((value) => this.valueInStock(optionIndex, value));
      this.selectedValues[optionIndex] = withStock[0] ?? values[0] ?? null;
    }

    // Refresca los swatches de una opción: oculta lo que no existe, marca como
    // agotado lo que no tiene stock y deja marcado el elegido.
    renderOption(optionIndex) {
      const group = this.groups[optionIndex];
      if (!group) return;

      group.querySelectorAll('input[type="radio"]').forEach((radio) => {
        const value = radio.value;
        const element = radio.closest(".swatch-element");
        const exists = this.valueExists(optionIndex, value);
        const inStock = this.valueInStock(optionIndex, value);

        if (element) {
          element.toggleAttribute("hidden", !exists);
          element.classList.toggle("soldout", !inStock);
          element.classList.toggle("available", inStock);
        }
        radio.disabled = !exists || !inStock;
        radio.checked = value === this.selectedValues[optionIndex];
      });

      const label = group.querySelector(".option-select-value");
      if (label) label.textContent = this.selectedValues[optionIndex] || "";
    }

    // Recalcula y repinta desde `fromPosition` (en prioridad) hacia abajo.
    cascadeFrom(fromPosition) {
      for (let position = fromPosition; position < this.priorityOrder.length; position++) {
        const optionIndex = this.priorityOrder[position];
        this.repairSelection(optionIndex);
        this.renderOption(optionIndex);
      }
    }

    /* ----------------------------- sincronizar con el tema (precio/carrito) --- */
    syncThemeAndUrl() {
      const variant = this.findSelectedVariant();
      if (!variant) return;
      this.pushSelectionToTheme(variant);
      this.reflectInUrl(variant);
      this.reflectInPrice(variant);
      this.notifyVariantChange(variant);
    }

    // Mueve los <select> nativos del tema y dispara "change" para que
    // Shopify.OptionSelectors actualice precio/carrito. Respaldo: <select> maestro.
    pushSelectionToTheme(variant) {
      const natives = [];
      let hasNatives = false;
      for (let i = 0; i < this.optionCount; i++) {
        natives[i] = document.getElementById(`product_select_${this.sectionId}-option-${i}`);
        if (natives[i]) hasNatives = true;
      }

      if (hasNatives) {
        natives.forEach((select, index) => { if (select) select.value = this.selectedValues[index]; });
        triggerChange(natives[this.optionCount - 1] || natives[0]);
      } else if (this.masterSelect) {
        this.masterSelect.value = variant.id;
        triggerChange(this.masterSelect);
      }
    }

    // Mantiene ?variant=… en la URL (precio/stock correctos al recargar).
    reflectInUrl(variant) {
      if (!(window.history && history.replaceState)) return;
      const url = new URL(window.location.href);
      url.searchParams.set("variant", variant.id);
      history.replaceState({}, "", url.toString());
    }

    // Actualiza el precio en pantalla. En este tema el nodo real es
    // #single_product__price-{sectionId} (span .product-price), que rellena el
    // selectCallback del tema; escribir aquí el mismo valor lo hace redundante
    // pero garantiza el precio aunque esa cadena no llegue a ejecutarse.
    reflectInPrice(variant) {
      if (!variant.price) return;
      const container = document.getElementById(`single_product__price-${this.sectionId}`);
      const priceEl = container
        ? container.querySelector(".product-price") || container
        : document.querySelector(".price, .product__price, .product-single__price");
      if (priceEl) priceEl.textContent = variant.price;
    }

    notifyVariantChange(variant) {
      document.dispatchEvent(new CustomEvent("variant:change", {
        detail: {
          sectionId: this.sectionId,
          variantId: variant.id,
          options: [...this.selectedValues],
          optionNames: this.optionNames,
        },
      }));
      this.emitGalleryUpdate();
    }

    // Publica (y cachea) las imágenes de los colores EN STOCK para la
    // condición/capacidad elegidas. Color es la opción de menor prioridad, así
    // que possibleValues da los colores compatibles con la selección actual; de
    // esos nos quedamos solo con los que tienen una variante disponible (los
    // agotados no aportan imágenes a la galería).
    emitGalleryUpdate() {
      if (this.suppressGalleryEmit) return; // el cambio lo originó la galería

      const colors = this.colorIndex === -1
        ? []
        : this.possibleValues(this.colorIndex)
            .filter((color) => this.valueInStock(this.colorIndex, color));

      const seen = new Set();
      const images = [];
      colors.forEach((color) => {
        (this.imagesByColor[color] || []).forEach((image) => {
          if (seen.has(image.id)) return;
          seen.add(image.id);
          images.push({ ...image, color });
        });
      });

      const selectedColor = this.colorIndex === -1 ? null : this.selectedValues[this.colorIndex];
      const selectedColorImages = (selectedColor && this.imagesByColor[selectedColor]) || [];
      const activeMediaId = selectedColorImages.length ? selectedColorImages[0].id : null;

      const detail = {
        sectionId: this.sectionId,
        color: selectedColor,
        colors,
        images,
        activeMediaId,
      };

      window.productGalleryState = window.productGalleryState || {};
      window.productGalleryState[this.sectionId] = detail;

      document.dispatchEvent(new CustomEvent("gallery:update", { detail }));
    }

    /* ------------------------------------------------------------- eventos --- */
    bindEvents() {
      Object.values(this.groups).forEach((group) => {
        group.addEventListener("input", (event) => {
          const radio = event.target;
          if (!radio || radio.type !== "radio") return;

          const optionIndex = Number(group.dataset.optionIndex);
          this.selectedValues[optionIndex] = radio.value;

          // Mantiene las de mayor prioridad; recalcula las de menor.
          this.cascadeFrom(this.priorityRank[optionIndex] + 1);
          this.renderOption(optionIndex);
          this.syncThemeAndUrl();
        });
      });

      document.addEventListener("gallery:select-color", this.handleGallerySelectColor);
    }

    // Navegación de la galería (swipe/thumb) → selecciona ese color sin devolver
    // un "slide" a la galería (ya está en la imagen correcta).
    handleGallerySelectColor(event) {
      if (!document.contains(this.firstGroup)) return; // instancia obsoleta (editor)
      const { detail } = event;
      if (!detail || detail.sectionId !== this.sectionId || this.colorIndex === -1) return;

      const { color } = detail;
      if (color == null || this.selectedValues[this.colorIndex] === color) return;

      this.selectedValues[this.colorIndex] = color;
      this.renderOption(this.colorIndex);

      this.suppressGalleryEmit = true;
      this.syncThemeAndUrl();
      this.suppressGalleryEmit = false;
    }
  }

  /* ----------------------------------------------------------- arranque --- */
  const boot = () => {
    const initialized = new Set();
    document.querySelectorAll(GROUP_SELECTOR).forEach((group) => {
      const sectionId = group.dataset.sectionId;
      if (initialized.has(sectionId) || group.__swatchInstance) return;
      initialized.add(sectionId);

      const instance = new SwatchCascade(sectionId);
      if (!instance.init()) return;

      document
        .querySelectorAll(`.product__cascade-swatch[data-section-id="${sectionId}"]`)
        .forEach((node) => { node.__swatchInstance = instance; });
    });
  };

  if (document.readyState !== "loading") boot();
  else document.addEventListener("DOMContentLoaded", boot);

  // Theme editor: re-inicializa al cargar la sección y limpia al descargarla.
  document.addEventListener("shopify:section:load", boot);
  document.addEventListener("shopify:section:unload", (event) => {
    event.target.querySelectorAll(GROUP_SELECTOR).forEach((node) => {
      if (!node.__swatchInstance) return;
      node.__swatchInstance.destroy();
      node.__swatchInstance = null;
    });
  });
})();
